import type React from 'react';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';

type Props<TTab extends string> = {
    className?: string;
    selectedTabKey: TTab;
    onTabChange: (tabId: TTab) => void;
    tabs: TTab[];
    getTabLabel: (tabId: TTab) => React.ReactNode;
};

export const TabsHeader = <TTab extends string = string>({
    className,
    tabs,
    getTabLabel,
    selectedTabKey,
    onTabChange,
}: Props<TTab>) => {
    const { ref, inView, entry } = useInView({ threshold: 1 });
    const { t } = useLocale();

    // Scroll active tab into view when selectedTabKey changes (only if visible)
    useEffect(() => {
        if (!entry?.target || !selectedTabKey || !inView) {
            return;
        }

        const activeTab = entry.target.querySelector('[data-active="true"]');
        if (activeTab) {
            activeTab.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    }, [selectedTabKey, inView, entry]);

    return (
        <div
            ref={ref}
            className={cn(
                'flex w-full border-b border-neutral-300 overflow-x-auto scrollbar-none',
                className,
            )}
        >
            <nav className="-mb-px flex gap-1 sm:gap-2 xl:gap-4" aria-label={t('aria__tabs')}>
                {tabs.map((tab) => {
                    const isActive = selectedTabKey === tab;

                    return (
                        <div
                            className={cn('flex border-b-2 border-transparent py-0.5', {
                                'border-neutral-700 font-medium': isActive,
                            })}
                            key={tab}
                            data-active={isActive}
                        >
                            <button
                                onClick={() => {
                                    onTabChange(tab);
                                }}
                                className={cn(
                                    'border-transparent text-neutral-700',
                                    'min-w-fit whitespace-nowrap border-b-2 text-sm leading-3 px-3 py-2 sm:px-2 sm:py-1.5 rounded-md',
                                    'hover:no-underline focus:outline-hidden hover:bg-neutral-100 hover:text-neutral-950',
                                    {
                                        'text-neutral-950': isActive,
                                    },
                                )}
                            >
                                {getTabLabel(tab)}
                            </button>
                        </div>
                    );
                })}
            </nav>
        </div>
    );
};
